/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { maxBy } from 'lodash';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { ContextualHelp, ShowMoreList, Typography } from '@coursera/cds-core';

import type { OccupationSkillAssessments } from 'bundles/account-profile/common/types';
import { getOccupationIdOrName } from 'bundles/account-profile/common/utils';
import type { OccupationIdToRoleMap } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import SkillsAssessmentBreakdown from 'bundles/account-profile/components/recruiting-profile-viewer/components/iMocha-skills/SkillsAssessmentBreakdown';
import SkillsScoreInfoDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/iMocha-skills/SkillsScoreInfoDialog';
import { doesOccupationHaveAssessmentResults } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/skillsUtils';

import _t from 'i18n!nls/account-profile';

const MAX_NUM_ITEMS = 2;

const styles = {
  headerWrapper: () => css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-wrap: wrap;
  `,
};

type Props = {
  occupationSkillsAssessments: OccupationSkillAssessments;
  idToRolesMap: OccupationIdToRoleMap;
  isRecruiter: boolean;
  editable: boolean;
};

const OccupationSkillsAssessmentsList = ({
  occupationSkillsAssessments,
  idToRolesMap,
  isRecruiter,
  editable,
}: Props) => {
  const track = useRetracked();

  if (!occupationSkillsAssessments || occupationSkillsAssessments.length === 0) {
    return null;
  }

  // I make a copy of the array to avoid sorting in place which sometimes throws an error
  const sortedSkillsAssessments = [...occupationSkillsAssessments].sort((assessment1, assessment2) => {
    const maxAssessment1Score =
      maxBy(assessment1.testResults, (testResult) => testResult.result?.score || 0)?.result?.score || 0;
    const maxAssessment2Score =
      maxBy(assessment2.testResults, (testResult) => testResult.result?.score || 0)?.result?.score || 0;

    return maxAssessment2Score - maxAssessment1Score;
  });

  function showSkillsAssessmentsList(
    skillsAssessment: (typeof sortedSkillsAssessments)[number],
    assessmentIndex: number
  ) {
    const occupation = skillsAssessment.occupation;
    const occupationNameOrId = getOccupationIdOrName(occupation);
    const desiredOccupation = idToRolesMap.get(occupation.id)?.occupationName ?? '';

    const sortedAndFilteredTestResults = [...skillsAssessment.testResults]
      .filter((testResult) => testResult.result?.score === 0 || !!testResult.result?.score)
      // At the time of writing, this sort doesn't do anything because testResults only has one element (since we only have one test per assessment).
      // If we ever add more tests to an assessment, this will sort them by score, so this acts as a future-proofing measure
      .sort((testResult1, testResult2) => {
        const test2Score = testResult2.result?.score || 0;
        const test1Score = testResult1.result?.score || 0;

        return test2Score - test1Score;
      });

    return sortedAndFilteredTestResults.map((testResult, testResultIndex) => {
      const isFirstCard = assessmentIndex === 0 && testResultIndex === 0;
      return (
        <ProfileViewerCard key={occupationNameOrId}>
          <div css={styles.headerWrapper}>
            <ContextualHelp
              helpToggleProps={{
                'aria-label': _t('Information about the skills proficiency section'),
              }}
              label={
                <Typography variant="h2semibold" data-testid="imocha-skills-section-title">
                  {desiredOccupation &&
                  doesOccupationHaveAssessmentResults(occupationSkillsAssessments, occupationNameOrId) ? (
                    <FormattedMessage
                      message={_t('Skills proficiency for {roleAndLevel}')}
                      roleAndLevel={<strong>{_t('Entry-level #{desiredOccupation}', { desiredOccupation })}</strong>}
                    />
                  ) : (
                    _t('Skills score analysis')
                  )}
                </Typography>
              }
            >
              {({ onClose, isExpanded = false }) => <SkillsScoreInfoDialog isOpen={isExpanded} onClose={onClose} />}
            </ContextualHelp>
          </div>

          <SkillsAssessmentBreakdown
            key={testResult.testId}
            testSummary={testResult}
            desiredOccupation={desiredOccupation}
            isRecruiter={isRecruiter}
          />

          {editable && isFirstCard}
        </ProfileViewerCard>
      );
    });
  }

  if (sortedSkillsAssessments.length > MAX_NUM_ITEMS) {
    return (
      <ShowMoreList
        controllerProps={{
          itemTypePlural: _t('skill proficiencies'),
          ariaLabel: _t('Show all #{numSkillAssessments} skill proficiencies', {
            numSkillAssessments: sortedSkillsAssessments.length,
          }),
        }}
        numItemsToShow={MAX_NUM_ITEMS}
        onChange={(isExpanded) => {
          if (isExpanded) {
            track({
              trackingData: {},
              // not tracked in eventing v3
              trackingName: 'profile_show_all_skill_assessments',
              action: 'click',
            });
            track({
              trackingData: {},
              // not tracked in eventing v3
              trackingName: 'profile_show_all_skill_assessments',
              action: 'view',
            });
          }
        }}
      >
        {sortedSkillsAssessments.map(showSkillsAssessmentsList)}
      </ShowMoreList>
    );
  } else {
    return <>{sortedSkillsAssessments.map(showSkillsAssessmentsList)}</>;
  }
};

export default OccupationSkillsAssessmentsList;
